import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add } from '@mui/icons-material'
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material'

import { isValidEmail } from '../../helpers/reportHelpers'

type ReportAllowedUsersFieldProps = {
  value: string[]
  onChange: (emails: string[]) => void
  disabled?: boolean
}

export const ReportAllowedUsersField: FC<ReportAllowedUsersFieldProps> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string>()

  const add = () => {
    const trimmed = email.trim()
    if (!isValidEmail(trimmed)) return setError(t('internal-analysts:reports_allowed_user_invalid'))
    if (value.includes(trimmed)) return setError(t('internal-analysts:reports_allowed_user_duplicate'))
    onChange([...value, trimmed])
    setEmail('')
    setError(undefined)
  }

  return (
    <Box>
      <Typography variant="body2" fontWeight="bold" mb={1}>
        {t('internal-analysts:reports_allowed_users')}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          type="email"
          label={t('internal-analysts:reports_allowed_user_email')}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
            setError(undefined)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              add()
            }
          }}
          error={!!error}
          helperText={error ?? t('internal-analysts:reports_allowed_users_help')}
          disabled={disabled}
          sx={{ minWidth: 320 }}
        />
        <Button variant="outlined" startIcon={<Add />} onClick={add} disabled={disabled || !email.trim()} sx={{ mt: 0.25 }}>
          {t('internal-analysts:reports_allowed_user_add')}
        </Button>
      </Stack>
      {value.length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
          {value.map((allowed) => (
            <Chip key={allowed} label={allowed} onDelete={disabled ? undefined : () => onChange(value.filter((user) => user !== allowed))} />
          ))}
        </Stack>
      )}
    </Box>
  )
}
